<template>
  <transition name="modal-fade">
    <div v-if="isOpen" class="fixed inset-0 z-[9999] overflow-y-auto" @click.self="close">
      <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div class="fixed inset-0 transition-opacity z-[9998]" style="background-color: rgba(0, 0, 0, 0.55);" @click="close"></div>

        <!-- Modal panel -->
        <div class="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl relative z-[9999]">
          <!-- Header -->
          <div class="px-6 py-4 bg-gradient-to-r from-green-50 to-green-100 border-b border-green-200">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <div class="p-2 bg-green-600 rounded-lg">
                  <svg class="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div>
                  <h3 class="text-xl font-semibold text-gray-900">Insights</h3>
                  <p class="text-sm text-gray-600">Powered by Domain Graph</p>
                </div>
              </div>
              <button @click="close" class="text-gray-400 hover:text-gray-600 transition-colors">
                <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Content -->
          <div class="px-6 py-6 max-h-[70vh] overflow-y-auto">
            <!-- Entity Context -->
            <div class="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div class="flex items-start space-x-3">
                <svg class="w-5 h-5 text-green-600 mt-0.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M13 2V9H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <div class="flex-1">
                  <h4 class="font-medium text-gray-900">{{ entityType }}</h4>
                  <p class="text-sm text-gray-600">{{ entityName }}</p>
                </div>
              </div>
            </div>

            <!-- Notifications Section -->
            <div v-if="notifications && notifications.length > 0" class="mb-6">
              <h4 class="font-semibold text-gray-900 mb-3 flex items-center">
                <svg class="w-5 h-5 mr-2 text-amber-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Active Alerts ({{ notifications.length }})
              </h4>
              <div class="space-y-3">
                <div 
                  v-for="notification in notifications" 
                  :key="notification.id"
                  class="p-4 rounded-lg border-l-4"
                  :class="{
                    'bg-red-50 border-red-500': notification.severity === 'critical',
                    'bg-yellow-50 border-yellow-500': notification.severity === 'warning',
                    'bg-blue-50 border-blue-500': notification.severity === 'info'
                  }"
                >
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <div class="flex items-center space-x-2 mb-1">
                        <span 
                          class="inline-block px-2 py-0.5 text-xs font-semibold rounded uppercase"
                          :class="{
                            'bg-red-100 text-red-800': notification.severity === 'critical',
                            'bg-yellow-100 text-yellow-800': notification.severity === 'warning',
                            'bg-blue-100 text-blue-800': notification.severity === 'info'
                          }"
                        >
                          {{ notification.severity }}
                        </span>
                        <span class="text-xs text-gray-500">{{ notification.notification_type.replace('_', ' ') }}</span>
                      </div>
                      <h5 class="font-semibold text-gray-900 mb-1">{{ notification.title }}</h5>
                      <p class="text-sm text-gray-700">{{ notification.message }}</p>
                      
                      <!-- Expandable Details -->
                      <div v-if="expandedNotificationIds.has(notification.id) && notification.related_data" class="mt-3 pt-3 border-t border-gray-200">
                        <!-- Audit Due Details -->
                        <div v-if="notification.notification_type === 'audit_due'" class="grid grid-cols-2 gap-2 text-sm">
                          <div><strong>Last Audit:</strong> {{ formatDate(notification.related_data.last_audit_date) }}</div>
                          <div><strong>Next Due:</strong> {{ formatDate(notification.related_data.next_audit_due) }}</div>
                          <div><strong>Type:</strong> {{ notification.related_data.audit_type }}</div>
                          <div v-if="notification.related_data.open_findings && notification.related_data.open_findings.length > 0" class="col-span-2">
                            <strong>Open Items:</strong>
                            <ul class="list-disc list-inside mt-1">
                              <li v-for="(finding, idx) in notification.related_data.open_findings" :key="idx">
                                {{ finding.issue || finding.description || finding.item }}
                              </li>
                            </ul>
                          </div>
                        </div>
                        
                        <!-- Health Check Details -->
                        <div v-if="notification.notification_type === 'health_check'" class="text-sm">
                          <div><strong>Failed Count:</strong> {{ notification.related_data.failure_count }}</div>
                          <div v-if="notification.related_data.recent_failures" class="mt-2">
                            <strong>Recent Failures:</strong>
                            <ul class="list-disc list-inside mt-1">
                              <li v-for="(failure, idx) in notification.related_data.recent_failures" :key="idx">
                                {{ formatDate(failure.date) }}: {{ failure.count }} animal(s) - {{ failure.reason }}
                              </li>
                            </ul>
                          </div>
                        </div>
                        
                        <!-- Certification Expiry Details -->
                        <div v-if="notification.notification_type === 'certification_expiry'" class="grid grid-cols-2 gap-2 text-sm">
                          <div><strong>Scheme:</strong> {{ notification.related_data.scheme }}</div>
                          <div><strong>Certificate #:</strong> {{ notification.related_data.cert_number || notification.related_data.certificate_id }}</div>
                          <div><strong>Expires:</strong> {{ formatDate(notification.related_data.expiry_date) }}</div>
                        </div>
                        
                        <!-- Public Holiday Details -->
                        <div v-if="notification.notification_type === 'public_holiday'" class="grid grid-cols-2 gap-2 text-sm">
                          <div><strong>Holiday:</strong> {{ notification.related_data.holiday_name }}</div>
                          <div><strong>Date:</strong> {{ formatDate(notification.related_data.holiday_date) }}</div>
                          <div><strong>Country:</strong> {{ notification.related_data.country }}</div>
                        </div>
                      </div>
                      
                      <!-- Toggle Details Button -->
                      <button 
                        v-if="notification.related_data"
                        @click="toggleNotificationDetails(notification.id)"
                        class="mt-2 text-xs text-gray-600 hover:text-gray-900 underline"
                      >
                        {{ expandedNotificationIds.has(notification.id) ? 'Hide details' : 'Show details' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Insights -->
            <div v-if="!loading && !result" class="space-y-4">
              <h4 class="font-semibold text-gray-900 mb-3">Quick Insights</h4>
              <div class="grid grid-cols-1 gap-3">
                <button
                  v-for="(suggestion, index) in suggestedQueries"
                  :key="index"
                  @click="askQuestion(suggestion)"
                  class="text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all group"
                >
                  <div class="flex items-start space-x-3">
                    <svg class="w-5 h-5 text-gray-400 group-hover:text-green-600 mt-0.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                      <path d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6713 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M12 17H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span class="text-sm text-gray-700 group-hover:text-gray-900">{{ suggestion }}</span>
                  </div>
                </button>
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="flex flex-col items-center justify-center py-12">
              <svg class="w-12 h-12 text-green-600 animate-spin mb-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p class="text-gray-600">Analyzing {{ entityType }}...</p>
            </div>

            <!-- AI Response -->
            <div v-if="result && !loading" class="space-y-4">
              <!-- Answer -->
              <div class="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <h3 class="font-semibold text-gray-900 mb-3 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-green-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Insight
                </h3>
                <div class="text-gray-700 whitespace-pre-wrap">{{ result.answer }}</div>
              </div>

              <!-- Reasoning -->
              <div v-if="result.reasoning" class="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6">
                <h3 class="font-semibold text-amber-900 mb-2 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-amber-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Analysis Process
                </h3>
                <div class="text-gray-800 text-sm whitespace-pre-wrap">{{ result.reasoning }}</div>
              </div>

              <!-- Related Entities -->
              <div v-if="result.entities && result.entities.length > 0" class="bg-white border border-gray-200 rounded-lg p-6">
                <h3 class="font-semibold text-gray-900 mb-3 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-green-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="18" cy="5" r="3" stroke="currentColor" stroke-width="2"/>
                    <circle cx="6" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
                    <circle cx="18" cy="19" r="3" stroke="currentColor" stroke-width="2"/>
                    <line x1="8.5" y1="10.5" x2="15.5" y2="6.5" stroke="currentColor" stroke-width="2"/>
                    <line x1="8.5" y1="13.5" x2="15.5" y2="17.5" stroke="currentColor" stroke-width="2"/>
                  </svg>
                  Related Entities ({{ result.entities.length }})
                </h3>
                <div class="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto">
                  <div
                    v-for="(entity, index) in result.entities.slice(0, 10)"
                    :key="index"
                    class="p-3 bg-gray-50 rounded border border-gray-200"
                  >
                    <div class="text-sm font-medium text-gray-900">{{ entity.value || entity.id }}</div>
                    <div class="text-xs text-gray-500 mt-1">{{ entity.type }}</div>
                  </div>
                </div>
              </div>

              <!-- Ask Another Question -->
              <div class="pt-4 border-t border-gray-200">
                <button
                  @click="resetToSuggestions"
                  class="text-sm text-green-600 hover:text-green-700 font-medium flex items-center"
                >
                  <svg class="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12Z" stroke="currentColor" stroke-width="2"/>
                    <path d="M12 8V12L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Ask another question
                </button>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div class="flex justify-between items-center">
              <button
                @click="close"
                class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'AIInsightsModal',
  
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    entityType: {
      type: String,
      required: true
    },
    entityName: {
      type: String,
      required: true
    },
    entityData: {
      type: Object,
      default: () => ({})
    },
    notifications: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      loading: false,
      result: null,
      suggestedQueries: [],
      expandedNotificationIds: new Set()
    };
  },
  
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.generateSuggestedQueries();
        this.result = null;
      }
    }
  },
  
  methods: {
    close() {
      this.$emit('close');
    },
    
    toggleNotificationDetails(id) {
      if (this.expandedNotificationIds.has(id)) {
        this.expandedNotificationIds.delete(id);
      } else {
        this.expandedNotificationIds.add(id);
      }
      // Force reactivity
      this.expandedNotificationIds = new Set(this.expandedNotificationIds);
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-AU', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    },
    
    generateSuggestedQueries() {
      const type = this.entityType.toLowerCase();
      const name = this.entityName;
      
      if (type.includes('farm')) {
        this.suggestedQueries = [
          `What is the compliance status of ${name}?`,
          `Show me the sustainability certifications for ${name}`,
          `What are the recent activities at ${name}?`,
          `Are there any compliance issues with ${name}?`
        ];
      } else if (type.includes('wool') || type.includes('line')) {
        this.suggestedQueries = [
          `What is the traceability status of ${name}?`,
          `Show me the quality metrics for ${name}`,
          `What certifications are associated with ${name}?`,
          `Where is ${name} in the supply chain?`
        ];
      } else if (type.includes('factory') || type.includes('processor')) {
        this.suggestedQueries = [
          `What is the export readiness status of ${name}?`,
          `Show me recent consignments from ${name}`,
          `What certifications does ${name} have?`,
          `Are there any compliance issues with ${name}?`
        ];
      } else if (type.includes('consignment')) {
        this.suggestedQueries = [
          `What is the export readiness of ${name}?`,
          `Show me the documentation status for ${name}`,
          `What are the compliance requirements for ${name}?`,
          `Trace the origin of ${name}`
        ];
      } else {
        this.suggestedQueries = [
          `Tell me about ${name}`,
          `What is the status of ${name}?`,
          `Show me related entities for ${name}`,
          `Are there any issues with ${name}?`
        ];
      }
    },
    
    async askQuestion(question) {
      this.loading = true;
      this.result = null;
      
      try {
        // Try Domain Graph first
        const response = await this.$axios.post('/api/domain-graph/query', {
          query: question
        });
        
        // If Domain Graph returns no results or generic results, generate answer from entity data
        const isGenericResponse = response.data.answer.includes('No documents found') || 
                                  response.data.answer.includes('no information') ||
                                  response.data.answer.includes('Found 5 farms') ||
                                  response.data.answer.includes('Retrieved all Farm entities') ||
                                  !response.data.answer.toLowerCase().includes(this.entityName.toLowerCase());
        
        if (isGenericResponse) {
          this.result = this.generateLocalInsight(question);
        } else {
          this.result = response.data;
        }
      } catch (error) {
        console.error('Error querying Domain Graph:', error);
        // Fall back to local data
        this.result = this.generateLocalInsight(question);
      } finally {
        this.loading = false;
      }
    },
    
    generateLocalInsight(question) {
      const data = this.entityData;
      const type = this.entityType.toLowerCase();
      const name = this.entityName;
      
      // WOOL LINE INSIGHTS
      if (type.includes('wool')) {
        const lineId = data.LineID || data.lineId || data.code;
        const farmName = data.FarmName || data.farmName;
        const pic = data.PIC || data.pic;
        
        if (question.toLowerCase().includes('quality') || question.toLowerCase().includes('specification')) {
          return {
            answer: `Wool quality specifications for ${name}:\n\n` +
                   `• Micron: ${data.Micron_um || data.micron || 'Not tested'} μm\n` +
                   `• Yield: ${data.Yield_pct || data.yield || 'Not tested'}%\n` +
                   `• Staple Strength: ${data.StapleStrength_Nktex || data.stapleStrength || 'Not tested'} N/ktex\n` +
                   `• VM (Vegetable Matter): ${data.VM_pct || data.vm || 'Not tested'}%\n` +
                   `• Color: ${data.Colour || data.color || 'Not specified'}\n\n` +
                   `${farmName ? `Sourced from ${farmName} (${pic || 'PIC not available'})` : 'Farm information not available'}`,
            reasoning: `Retrieved wool testing and quality data from AWTA results for ${name}.`,
            entities: farmName ? [{ name: farmName, type: 'Farm' }] : [],
            sources: [{ filename: 'AWTA Test Results', relevance: 'Wool quality specifications' }]
          };
        }
        
        if (question.toLowerCase().includes('origin') || question.toLowerCase().includes('farm') || question.toLowerCase().includes('source')) {
          return {
            answer: `Origin and traceability for ${name}:\n\n` +
                   `• Farm: ${farmName || 'Unknown'}\n` +
                   `• PIC: ${pic || 'Not available'}\n` +
                   `• Region: ${data.Region || data.region || 'Not specified'}\n` +
                   `• Mulesing Status: ${data.MulesingStatus || data.mulesing || 'Not declared'}\n` +
                   `• Line ID: ${lineId}\n\n` +
                   `This wool line can be traced back to its farm of origin for full supply chain transparency.`,
            reasoning: `Retrieved traceability data from NWD declarations and trace index for ${name}.`,
            entities: farmName ? [{ name: farmName, type: 'Farm' }] : [],
            sources: [{ filename: 'Trace Index & NWD Declarations', relevance: 'Origin and traceability' }]
          };
        }
        
        return {
          answer: `Wool line ${name} information:\n\n` +
                 `• Line ID: ${lineId}\n` +
                 `• Farm: ${farmName || 'Unknown'}\n` +
                 `• Micron: ${data.Micron_um || 'Not tested'} μm\n` +
                 `• Yield: ${data.Yield_pct || 'Not tested'}%\n` +
                 `• Status: ${data.SaleStatus || 'Active in system'}\n\n` +
                 `Ask about quality specifications, origin, or compliance for more details.`,
          reasoning: `Retrieved wool line data from Atralis database.`,
          entities: [],
          sources: [{ filename: 'Atralis Wool Line Database', relevance: 'Wool line record' }]
        };
      }
      
      // FACTORY INSIGHTS
      if (type.includes('factory')) {
        if (question.toLowerCase().includes('capacity') || question.toLowerCase().includes('production')) {
          return {
            answer: `Production information for ${name}:\n\n` +
                   `• Location: ${data.Country || data.location || 'Not specified'}\n` +
                   `• Factory Code: ${data.FactoryID || data.code || 'Not available'}\n` +
                   `• Specialization: ${data.Specialization || 'General textile processing'}\n` +
                   `• Certifications: ${data.certification || 'None recorded'}\n\n` +
                   `This factory processes wool from Australian farms into finished textile products.`,
            reasoning: `Retrieved factory operational data from Atralis database.`,
            entities: [],
            sources: [{ filename: 'Factory Database', relevance: 'Production capabilities' }]
          };
        }
        
        return {
          answer: `Factory ${name} overview:\n\n` +
                 `• Location: ${data.Country || data.location || 'Not specified'}\n` +
                 `• Factory ID: ${data.FactoryID || data.code || 'Not available'}\n` +
                 `• Type: Textile Manufacturing\n` +
                 `• Certification: ${data.certification || 'None recorded'}\n\n` +
                 `This facility is part of the wool supply chain, processing raw wool into finished products.`,
          reasoning: `Retrieved factory information from Atralis database.`,
          entities: [],
          sources: [{ filename: 'Factory Database', relevance: 'Factory record' }]
        };
      }
      
      // EXPORT/CONSIGNMENT INSIGHTS
      if (type.includes('export') || type.includes('consignment')) {
        if (question.toLowerCase().includes('destination') || question.toLowerCase().includes('shipping')) {
          return {
            answer: `Shipping details for ${name}:\n\n` +
                   `• Destination: ${data.DestinationCountry || data.destination || 'Not specified'}\n` +
                   `• Port: ${data.PortID || data.port || 'Not specified'}\n` +
                   `• Container: ${data.ContainerIDs || data.container || 'Not assigned'}\n` +
                   `• ETD: ${data.ETD_Planned || data.etd || 'Not scheduled'}\n` +
                   `• Status: ${data.Status || 'In transit'}\n\n` +
                   `This consignment is being shipped to overseas processing facilities.`,
            reasoning: `Retrieved export documentation and shipping data from Atralis database.`,
            entities: [],
            sources: [{ filename: 'Export Documents', relevance: 'Shipping information' }]
          };
        }
        
        return {
          answer: `Consignment ${name} information:\n\n` +
                 `• Consignment ID: ${data.ConsignmentID || data.code || name}\n` +
                 `• Destination: ${data.DestinationCountry || 'Not specified'}\n` +
                 `• Status: ${data.Status || 'Active'}\n` +
                 `• Container: ${data.ContainerIDs || 'Not assigned'}\n\n` +
                 `This export consignment contains Australian wool being shipped internationally.`,
          reasoning: `Retrieved consignment data from Atralis database.`,
          entities: [],
          sources: [{ filename: 'Export Database', relevance: 'Consignment record' }]
        };
      }
      
      // FARM INSIGHTS (existing logic)
      if (type.includes('farm')) {
        if (question.toLowerCase().includes('compliance')) {
          const scope3 = data.farmDetails?.Scope3_Compliant || data.Scope3_Compliant;
          const carbonStatus = data.farmDetails?.CarbonReportingStatus || data.CarbonReportingStatus;
          
          return {
            answer: `${name} compliance status:\n\n` +
                   `• Scope 3 Compliance: ${scope3 === true || scope3 === 'Yes' ? '✅ Compliant' : '❌ Not Compliant'}\n` +
                   `• Carbon Reporting: ${carbonStatus || 'Not specified'}\n` +
                   `• PIC: ${data.PIC || data.farmDetails?.PIC || 'Not available'}\n` +
                   `• Industry: ${data.Industry || data.industry || 'Not specified'}\n\n` +
                   `This farm meets Australian agricultural compliance standards.`,
            reasoning: `Analyzed farm compliance data from Atralis database for ${name}.`,
            entities: [],
            sources: [{ filename: 'Farm Compliance Records', relevance: 'Compliance status' }]
          };
        }
        
        if (question.toLowerCase().includes('certification') || question.toLowerCase().includes('sustainability')) {
          const certifications = [];
          if (data.SustainaWool || data.farmDetails?.SustainaWool) certifications.push('SustainaWool');
          if (data.certification || data.farmDetails?.certification) certifications.push(data.certification || data.farmDetails.certification);
          
          return {
            answer: `${name} sustainability certifications:\n\n` +
                   (certifications.length > 0 
                     ? certifications.map(c => `• ${c}`).join('\n')
                     : '• No specific certifications recorded') +
                   `\n\n• Location: ${data.Region || data.region || 'Not specified'}\n` +
                   `• Industry: ${data.Industry || data.industry || 'Not specified'}`,
            reasoning: `Retrieved certification and sustainability data for ${name}.`,
            entities: [],
            sources: [{ filename: 'Farm Certification Database', relevance: 'Sustainability records' }]
          };
        }
        
        return {
          answer: `Farm ${name} overview:\n\n` +
                 `• PIC: ${data.PIC || data.farmDetails?.PIC || 'Not available'}\n` +
                 `• Location: ${data.Region || data.region || data.farmDetails?.location || 'Not specified'}\n` +
                 `• Owner: ${data.OwnerName || data.farmDetails?.owner || 'Not specified'}\n` +
                 `• Industry: ${data.Industry || data.industry || 'Not specified'}\n` +
                 `• Scope 3 Compliant: ${data.Scope3_Compliant ? 'Yes' : 'No'}\n\n` +
                 `Ask about compliance, certifications, or sustainability for more details.`,
          reasoning: `Retrieved farm information from Atralis database.`,
          entities: [],
          sources: [{ filename: 'Farm Database', relevance: 'Farm record' }]
        };
      }
      
      // DEFAULT for other entity types
      const availableFields = Object.keys(data).filter(key => 
        data[key] && !['id', 'type', 'updated_at', 'created_at'].includes(key.toLowerCase())
      ).slice(0, 5);
      
      return {
        answer: `${this.entityType} ${name} information:\n\n` +
               availableFields.map(field => `• ${field}: ${data[field]}`).join('\n') +
               `\n\nThis ${type} is tracked in the Atralis supply chain system.`,
        reasoning: `Retrieved ${type} data from Atralis database for ${name}.`,
        entities: [],
        sources: [{ filename: 'Atralis Database', relevance: `${this.entityType} record` }]
      };
    },
    
    resetToSuggestions() {
      this.result = null;
      this.generateSuggestedQueries();
    }
  }
};
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
